import { MoreVertical } from "lucide-react";
import { PsvMonitoringProgramStatus } from "@api/shared-runtime/psv-monitoring";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  PROGRAM_STATUS_BADGE,
  PROGRAM_STATUS_LABEL,
} from "../monitoring-constants";
import { summarizeSources, summarizeTargets } from "../monitoring-format";
import type { MonitoringProgram } from "../monitoring-types";
import { MonitoringStatusBadge } from "../monitoring-status-badge";

type Props = {
  programs: MonitoringProgram[];
  /** Archived rules are read-only and have no actions. */
  readOnly?: boolean;
  canEdit: boolean;
  onEdit: (program: MonitoringProgram) => void;
  onSetStatus: (input: {
    program: MonitoringProgram;
    status: PsvMonitoringProgramStatus;
  }) => void;
};

export function RulesTable({
  programs,
  readOnly = false,
  canEdit,
  onEdit,
  onSetStatus,
}: Props) {
  const showActions = canEdit && !readOnly;
  return (
    <Card className="overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead>Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Sources checked</TableHead>
            <TableHead>Covers</TableHead>
            {showActions && (
              <TableHead>
                <span className="sr-only">Actions</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {programs.map((program) => (
            <TableRow key={program.id}>
              <TableCell className="font-medium">
                {showActions ? (
                  <button
                    type="button"
                    className="-mx-1 min-h-11 rounded-sm px-1 text-left hover:text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary sm:min-h-0"
                    aria-label={`Edit ${program.name}`}
                    onClick={() => onEdit(program)}
                  >
                    {program.name}
                  </button>
                ) : (
                  program.name
                )}
              </TableCell>
              <TableCell>
                <MonitoringStatusBadge
                  kind={PROGRAM_STATUS_BADGE[program.status]}
                  label={PROGRAM_STATUS_LABEL[program.status]}
                />
              </TableCell>
              <TableCell className="max-w-xs whitespace-normal text-sm text-muted-foreground">
                {summarizeSources(program.sources)}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {summarizeTargets(program.targets)}
              </TableCell>
              {showActions && (
                <TableCell className="text-right">
                  <RuleActions
                    program={program}
                    onEdit={onEdit}
                    onSetStatus={onSetStatus}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function RuleActions({
  program,
  onEdit,
  onSetStatus,
}: {
  program: MonitoringProgram;
  onEdit: Props["onEdit"];
  onSetStatus: Props["onSetStatus"];
}) {
  const isActive = program.status === PsvMonitoringProgramStatus.Active;
  const toggleStatus = isActive
    ? PsvMonitoringProgramStatus.Paused
    : PsvMonitoringProgramStatus.Active;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="max-sm:size-11"
          aria-label={`Actions for ${program.name}`}
        >
          <MoreVertical size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onEdit(program)}>
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onSetStatus({ program, status: toggleStatus })}
        >
          {isActive ? "Pause" : "Resume"}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() =>
            onSetStatus({
              program,
              status: PsvMonitoringProgramStatus.Archived,
            })
          }
        >
          Archive
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
